import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ClipboardCheck, Play } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError, type ListResponse } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLocations } from '../lib/queries';
import { formatDateTime } from '../lib/format';
import { EmptyState, PageHeader, Spinner } from '../components/ui';

interface InventorySession {
  id: number;
  location: { id: number; name: string };
  started_by: string | null;
  started_at: string;
  closed_at: string | null;
  note: string | null;
  expected_count: number;
  confirmed_count: number;
  missing_count: number;
  unexpected_count: number;
}

/** Inventuren starten und frühere Durchgänge nachlesen. */
export function InventoryPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locations = useLocations();
  const [locationId, setLocationId] = useState(0);

  const sessions = useQuery({
    queryKey: ['inventory-sessions'],
    queryFn: () => api<ListResponse<InventorySession>>('/inventory', { query: { limit: 50 } }),
  });

  const start = useMutation({
    mutationFn: () =>
      api<InventorySession>('/inventory', { method: 'POST', body: { location_id: locationId } }),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['inventory-sessions'] });
      toast.success(`Inventur für ${session.location.name} gestartet`);
      void navigate({ to: '/inventur/$sessionId', params: { sessionId: String(session.id) } });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Start fehlgeschlagen'),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Bestandsprüfung"
        title="Inventur"
        subtitle="Standort wählen, Geräte durchscannen, Abweichungen sehen."
      />

      {auth.can('inventory.write') && (
        <section className="surface animate-fade-up mb-6 p-5">
          <span className="label">Neue Inventur</span>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              className="field"
              value={locationId}
              onChange={(event) => setLocationId(Number(event.target.value))}
              aria-label="Standort wählen"
            >
              <option value={0}>— Standort wählen —</option>
              {locations.data?.rows.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => start.mutate()}
              disabled={!locationId || start.isPending}
              className="btn btn-accent btn-md shrink-0"
            >
              <Play className="h-4 w-4" aria-hidden />
              Starten
            </button>
          </div>
        </section>
      )}

      {sessions.isLoading && <Spinner />}

      {sessions.data?.rows.length === 0 && (
        <EmptyState
          title="Noch keine Inventur"
          description="Ein Durchgang dauert bei rund hundert Geräten wenige Minuten."
        />
      )}

      <ul className="stagger space-y-3">
        {sessions.data?.rows.map((session) => (
          <li key={session.id}>
            <Link
              to="/inventur/$sessionId"
              params={{ sessionId: String(session.id) }}
              className="surface card-link flex flex-wrap items-center justify-between gap-3 px-5 py-4 no-underline"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-[0.9375rem] font-semibold">
                  <ClipboardCheck className="h-4 w-4 text-[var(--color-fg3)]" aria-hidden />
                  {session.location.name}
                </span>
                <span className="block text-sm text-[var(--color-fg3)]">
                  {formatDateTime(session.started_at)}
                  {session.started_by ? ` · ${session.started_by}` : ''}
                </span>
              </span>

              <span className="flex items-center gap-2">
                <span className="pill pill-deployed tabular">{session.confirmed_count} bestätigt</span>
                {session.missing_count > 0 && (
                  <span className="pill pill-stock tabular">{session.missing_count} offen</span>
                )}
                {session.unexpected_count > 0 && (
                  <span className="pill pill-sold tabular">
                    {session.unexpected_count} unerwartet
                  </span>
                )}
                {session.closed_at && <span className="pill pill-archived">abgeschlossen</span>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
